// oxlint-disable jsx-no-literals
import { Link } from "react-router";

import { Card } from "@/components/Card";
import { ContentFull, ContentLayout } from "@/components/ContentLayout";
import { FullScreenLayout } from "@/components/FullScreenLayout";

export function TermsOfServicePage() {
  return (
    <FullScreenLayout>
      <ContentLayout wet title={<Link to="/terms-of-service">Terms Of Service</Link>}>
        <ContentFull className="pt-26">
          <Card>
            <article className="flex flex-col gap-4 p-4">
              <h1 className="font-title">Terms Of Service</h1>

              <p>
                Welcome to Bale. By using the Bale website (our &quot;Website&quot;), mobile application (our
                &quot;App&quot;) and our services (together with the Site and App, the “Services”) you are agreeing to
                these legally binding rules (the &quot;Terms of Service&quot;). You are also agreeing to our{" "}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>

              <p>
                We may update the Terms of Service from time to time and you are responsible for periodically reviewing
                the most current version of the Terms of Service on our Website. Your continued use of the Services or
                submission of information will be deemed your conclusive acceptance of our updated Terms of Service.
              </p>

              <h2 className="font-subtitle">Ownership/Restrictions on Use</h2>

              <p>
                All text, data, graphics, photographs, images, audio, video, trademarks, service marks, trade names and
                other information, visual or other digital material, software (including source and object codes) and
                all other content of any description available through our Services, or available via a link from our
                Services to a page created by Bale on another website (collectively, the &quot;Content&quot;), are the
                sole property of Bale. All Content is protected by Canadian and international copyright, trade-mark,
                service marks, patents, trade secrets and other proprietary rights and laws. Use of the Content for any
                purpose not expressly permitted in our Terms of Service or otherwise consented to by Bale is prohibited.
                You may download or print one copy of the Content to any single device for your personal,
                non-commercial, informational use only, provided that you keep intact all copyright and other
                proprietary notices. You may not otherwise copy, reproduce, perform, distribute, display or create
                derivative works of the Content. Reproduction of multiple copies of the Content, in whole or in part,
                for resale or distribution is strictly prohibited except with the prior written permission of Bale. To
                obtain written consent for such reproduction, please contact us at:{" "}
                <a href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL}`}>{import.meta.env.VITE_SUPPORT_EMAIL}</a>.
              </p>

              <p>
                Our Services are licensed, not sold. We grant you a non-exclusive, non-transferable, limited licence to
                install our App, or access our Website, on any single mobile device, tablet, or similar technology
                solely to be used in connection with our Services for your private, personal, use. Our Services are
                protected by copyright and other intellectual property laws and treaties and are owned by us or our
                service providers. You agree that you will not copy, attempt to reverse engineer, modify, translate or
                disassemble our Services in whole or in part. You may not use or export or re-export our Services except
                as permitted under the law of the United States and Canada and the laws of the jurisdiction in which our
                Services were obtained. We may automatically check and update the version of our Services which you are
                using in order to improve the performance and capabilities of our Services.
              </p>

              <h2 className="font-subtitle">Creating an Account</h2>

              <p>
                In order to use the services, you must create an account (your &quot;Account&quot;) using your email
                address. The email address you provide will be how you access your Account. You alone are responsible
                for keeping your Account login information and associated passwords confidential, and for any and all of
                your Account activity. Therefore, you should protect your password and make your password secure and
                difficult for others to guess. You agree to immediately notify us of any unauthorized use of your
                Account or any other breach of security via{" "}
                <a href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL}`}>{import.meta.env.VITE_SUPPORT_EMAIL}</a>.
              </p>

              <h2 className="font-subtitle">Code of Conduct</h2>
              <p>
                You must adhere to our code of conduct, as described herein (the &quot;Code of Conduct&quot;) as a
                condition of your continued access to and use of our Services. Any breach of this Code of Conduct, in
                our sole discretion, will result in your access to your Account being revoked.
              </p>

              <p>You agree not to:</p>
              <ol className="list-inside list-decimal">
                <li>
                  Register under a false identity, impersonate any person or entity, or falsely state or otherwise
                  misrepresent your affiliation with a person or entity;
                </li>
                <li>
                  Submit information you know to be false, misleading, or inaccurate. This includes, but is not limited
                  to events or gatherings you are not hosting or participating in, or otherwise do not exist.
                </li>
                <li>Submit or upload files or information that is illegal, or promotes illegal behaviour.</li>
                <li>
                  Harvest or otherwise collect or store any information (including personal information) about other
                  users of our Services, including email addresses, without the express consent of such users;
                </li>
                <li>
                  Use any robot, spider, scraper or other automated means to access our Services and collect content for
                  any purpose without our express written permission;
                </li>
                <li>
                  Attempt to gain unauthorized access to our Services, other computer systems, or networks connected to
                  our services through password mining or other means;
                </li>
                <li>Interfere with or disrupt networks or servers connected to our Services;</li>
                <li>
                  Take any action that imposes, or may impose (as determined by us in our sole discretion) an
                  unreasonable or disproportionately large load on our infrastructure;
                </li>
                <li>Facilitate or encourage any of the above conduct.</li>
              </ol>

              <h2 className="font-subtitle">Our rights</h2>
              <p>Bale reserves these rights:</p>
              <ul className="list-inside list-disc">
                <li>
                  We can make changes to or discontinue, temporarily or permanently, the Bale Services without notice or
                  liability.
                </li>
                <li>
                  We have the right to decide who is eligible to use Bale. We can cancel accounts or decline to offer
                  our services. We can change our eligibility criteria at any time.
                </li>
                <li>
                  We have the right to modify or remove content deemed to violate the Code of Conduct, at our own
                  discretion.
                </li>
              </ul>

              <p>
                Bale is not liable for any damages as a result of any of these actions, and it is our policy not to
                comment on the reasons for any such action.
              </p>

              <h2 className="font-subtitle">Intellectual Property Infringement</h2>

              <p>
                If you are an owner of intellectual property (or the owner&apos;s authorized agent) and believe that any
                of our Content infringes your intellectual property, please notify us using by sending a written notice
                of intellectual property infringement to:{" "}
                <a href={`mailto:${import.meta.env.VITE_SUPPORT_EMAIL}`}>{import.meta.env.VITE_SUPPORT_EMAIL}</a>. In
                your written notice, please provide the following information:
              </p>

              <ul className="list-inside list-disc">
                <li>Identification of the intellectual property claimed to have been infringed;</li>
                <li>
                  Information sufficient to permit us to contact you, such as an address, telephone number, and, if
                  available, an email address at which you may be contacted;
                </li>
                <li>
                  A statement that you believe that use of the Contents in the manner complained of is not authorized by
                  the owner, its agent, or the law; and
                </li>
                <li>
                  A statement that the information in the notification is accurate, and under penalty of perjury, that
                  you are the owner or are authorized to act on behalf of the owner of an exclusive right that is
                  allegedly infringed.
                </li>
              </ul>
              <h2 className="font-subtitle">Links to Third-Party Websites</h2>
              <p>
                We may offer links on our Services to websites and mobile applications owned and operated by third
                parties. We provide these links as a convenience to our visitors. We do not review the content of such
                third party services, and neither endorse, nor are responsible for, any content, advertising, products,
                services or other materials on or available from such third party services. You assume full
                responsibility for your use of third party services. Such services may be governed by terms and
                conditions different from those applicable to our Services, and we encourage you to review the terms and
                privacy policies of those third parties before using their services. We may also offer links to Content
                created by us and available on other services. If you link to that Content you are responsible for
                ensuring that you comply with the terms of use applicable to those services while you are using them.
              </p>

              <h2 className="font-subtitle">Warranty Disclaimer</h2>

              <p>
                <strong>
                  You use our Services solely at your own risk. They are provided to you &quot;as is&quot; and &quot;as
                  available&quot; and <em>without warranty of any kind</em>, express or implied.
                </strong>
              </p>

              <p>
                <strong>
                  We do not warrant that the content or user content is accurate, reliable, or correct; that our
                  Services will be available at any particular time or location; that your access to our Services will
                  be uninterrupted; that any defects or errors will be corrected; that our Services are free of viruses
                  or other harmful components; or that the content is timely, secure or error-free. To the maximum
                  extent permitted by law, Bale disclaims all warranties and conditions with respect to its Services,
                  including any warranties of merchantability, merchantable quality, non-infringement, and fitness for a
                  particular purpose.
                </strong>
              </p>

              <h2 className="font-subtitle">Limitation of Liability</h2>

              <p>
                To the fullest extent permissible by law, in no event will Bale, its employees, or content providers be
                liable for any indirect, incidental, punitive, consequential, special, or exemplary damages of any kind,
                including but not limited to damages
              </p>
              <ol className="list-inside list-decimal">
                <li>resulting from your access to, use of, or inability to access or use our Services;</li>
                <li>For any lost profits or data lost;</li>
                <li>for any conduct of content of any third party on the Services.</li>
              </ol>

              <p>
                In no event shall Bale&apos;s liability for direct damages be in excess of (in the aggregate) one
                hundred Canadian Dollars ($100.00).
              </p>

              <p>
                You will indemnify and hold harmless Bale and its affiliates and our and their respective officers,
                directors, agents, and employees (each an &quot;Indemnified Party&quot;), from any claim made by any
                third part, together with any amounts payable to the third part whether in settlement or as may
                otherwise be awarded, and reasonable legal costs incurred by any of the Indeminified Parties, arising
                from or relating to your use of our Services, and any alleged violation by you of any applicable law or
                regulation. We reserve the right, at our own expense, to assume the exclusive defense and control of any
                matter subject to indemnification by you, but doing so will not excuse your indemnity obligations.
              </p>

              <h2 className="font-subtitle">Choice of Law and Forum</h2>

              <p>
                Where permitted by law, our Terms of Service are governed by and will be interpreted in accordance with
                the laws of the Province of Ontario and of the laws of Canada applicable therein, without regard to any
                principles of conflicts of law. Where permitted by law, you agree that any action to enforce these Terms
                of Service may be brought in the courts located in the Province of Ontario. You further agree to submit
                to the personal jurisdiction of these courts for the purpose of any proceeding arising out of these
                Terms of Service and waive any objections and defenses inconsistent with such venue. By using our
                Services, you represent and warrant that your use complies with applicable law in your jurisdiction of
                residence.
              </p>

              <h2 className="font-subtitle">Entire Agreement</h2>

              <p>
                These Terms of Service constitute the entire agreement between you and us with respect to your use of
                our Services. If any provision of these Terms of Service is held to be invalid or unenforceable, such
                provision will be stricken and the remaining provisions enforced. Notwithstanding any other provisions
                of these Terms of Service, any provision of these Terms of Service that imposes or contemplates
                continuing rights or obligations on you or us will survive the expiration or termination of these Terms
                of Service, including, without limitation, the indemnification and limitation of liability provisions.
              </p>

              <p>
                It is the express wish of the parties that this agreement and all related documents be drawn up in
                English. C’est la volonté expresse des parties que la présente convention ainsi que les documents qui
                s’y rattachent soient rédigés en anglais.
              </p>

              <p>
                The failure of either you or Bale to exercise any right provided for in these Terms of Service in any
                way won’t be deemed a waiver of any other rights.
              </p>

              <p>
                These Terms are personal to you. You can’t assign them, transfer them, or sublicense them unless you get
                Bale&apos;s prior written consent. Bale has the right to assign, transfer, or delegate any of its rights
                and obligations under these Terms of Service without your consent. Bale will provide you notice via
                email, written notice, or by conspicuously posting the notice on our Site.
              </p>
            </article>
          </Card>
        </ContentFull>
      </ContentLayout>
    </FullScreenLayout>
  );
}
